import { nextTick } from 'vue';
import axios from 'axios';
import dayjs from 'dayjs';
import Qs from 'qs';
import { message } from 'ant-design-vue';
import basicStorage from '@/utils/basicStorage';
import { getAuthSessionEpoch } from '@/utils/authSession';
import { userStore } from '@/store/user';
import router from '@/router';
import { resetRealtimeSession } from '@/utils/useWsClient';

// 常量定义 - 导出TOKEN供外部使用
export const TOKEN = 'token';
const TOKEN_LOST_MINUTES = 31;
const TIMEOUT_DURATION = 60 * 1000;
const AUTH_HEADER = 'Authorization';
const TOKEN_TIME_KEY = 'token-time';
const REQUEST_AUTH_EPOCH_KEY = '__requestAuthEpoch';

// 特殊URL列表（不进行常规响应处理的接口）
const SPECIAL_URLS = [
  '/api/hr/demand/templateExport',
  '/api/v1/system/fileCenter/downloadTemplateFile/IMPORT_USER/XLSX',
  '/api/v1/system/fileCenter/downloadErrorFile',
  '/api/v1/system/fileCenter/downloadOriginalFile',
  '/api/v1/system/fileCenter/downloadExportFile',
];

// 需要重新登录的错误码
const RELOGIN_ERROR_CODES = new Set(['103101']);
const AUTH_ERROR_STATUS = 401;
const FORBIDDEN_STATUS = 403;
const SERVER_ERROR_STATUS = 500;
const GATEWAY_TIMEOUT_STATUS = 504;

// 创建axios实例
const service = axios.create({
  timeout: TIMEOUT_DURATION,
  paramsSerializer: {
    serialize: params => Qs.stringify(params, { allowDots: true }),
  },
});

/**
 * 退出登录处理函数（内部使用）
 * 清除Token并刷新页面
 */
const performLogout = async () => {
  const resetRealtimePromise = resetRealtimeSession();
  basicStorage.removeItem(TOKEN);
  basicStorage.removeItem(TOKEN_TIME_KEY);
  // 重置路由标签页
  const store = userStore();
  store.panes = [
    {
      title: '欢迎页',
      path: '/layout/welcome',
      name: 'welcome',
      closable: false,
    },
  ];
  // 清空菜单数据
  store.menus = null;
  // 重置全局状态
  store.reset();
  await resetRealtimePromise;
  // 等待状态更新完成
  await nextTick();
  // 刷新页面
  router.push({ name: 'login' });
};

/**
 * 添加请求拦截器
 * 1. 添加认证头
 * 2. 添加防缓存时间戳
 */
service.interceptors.request.use(
  config => {
    // 处理路径：可根据后端实际路径调整
    if (config.url?.startsWith('/system')) {
      config.url = config.url.replace('/system', '/api/boot/system');
    }

    // 记录发起请求时的登录身份，防止旧账号的迟到响应覆盖新账号状态。
    const requestAuthToken = basicStorage.getItem(TOKEN) || '';
    config[REQUEST_AUTH_EPOCH_KEY] = getAuthSessionEpoch();
    config.headers[AUTH_HEADER] = requestAuthToken;

    // 添加防缓存时间戳
    config.params = {
      ...config.params,
      timestamp: Date.now(),
    };

    return config;
  },
  error => Promise.reject(error)
);

/**
 * 响应拦截器
 * 处理响应数据和错误
 */
service.interceptors.response.use(
  response => handleResponseSuccess(response),
  error => handleResponseError(error)
);

// 响应成功处理
const handleResponseSuccess = response => {
  const currentAuthRequest = isCurrentAuthRequest(response.config);
  // 更新Token及过期时间
  if (
    response.headers[AUTH_HEADER.toLowerCase()] &&
    currentAuthRequest
  ) {
    updateToken(
      response.headers[AUTH_HEADER.toLowerCase()],
      dayjs().add(TOKEN_LOST_MINUTES, 'minute').format('YYYY-MM-DD HH:mm:ss')
    );
  }
  if (!currentAuthRequest) return Promise.reject(response);

  // 特殊URL或文件下载类型直接返回
  if (isSpecialUrl(response.config.url) || response.config.fileDownLoadType === 'stream') {
    return response;
  }

  // 成功响应处理
  if (response.data.code === '000000') {
    return response;
  }

  // 业务错误处理
  return Promise.reject(new Error(response.data.message || 'Unknown error'));
};

// 响应错误处理
const handleResponseError = error => {
  console.error('API Error:', error);

  const { response } = error;
  const currentAuthRequest = isCurrentAuthRequest(response?.config || error.config);

  // 更新Token（如果存在）
  if (
    response?.headers[AUTH_HEADER.toLowerCase()] &&
    currentAuthRequest
  ) {
    updateToken(response.headers[AUTH_HEADER.toLowerCase()]);
  }
  if (!currentAuthRequest) return Promise.reject(response || error);

  // 静默请求也不能绕过认证失效处理。
  if (response?.status === AUTH_ERROR_STATUS) {
    handleAuthError(response.data, response.config);
    return Promise.reject(response);
  }

  // 静默模式：跳过 toast 提示
  if (response?.config?.silent || error.config?.silent) {
    return Promise.reject(response || error);
  }

  // 无响应处理
  if (!response) {
    message.warning('网络连接异常，请稍后重试', 5);
    return Promise.reject(error);
  }

  const { status, data } = response;

  // 特定状态码处理
  switch (status) {
    case FORBIDDEN_STATUS:
      message.warning(data?.message || '无访问权限', 5);
      break;
    case SERVER_ERROR_STATUS:
      message.warning('服务异常，请稍后再试', 5);
      break;
    case GATEWAY_TIMEOUT_STATUS:
      handleTimeoutError(error);
      break;
    default:
      message.warning(data?.message || `请求错误 (${status})`, 5);
  }

  return Promise.reject(response);
};

// 辅助函数：更新Token
const updateToken = (token, expireTime = null) => {
  basicStorage.setItem(TOKEN, token);
  if (expireTime) {
    basicStorage.setItem(TOKEN_TIME_KEY, expireTime);
  }
};

const isCurrentAuthRequest = config =>
  config?.[REQUEST_AUTH_EPOCH_KEY] === getAuthSessionEpoch();

// 辅助函数：检查是否为特殊URL
const isSpecialUrl = url => {
  return SPECIAL_URLS.some(specialUrl => url?.includes(specialUrl));
};

// 辅助函数：处理认证错误
const handleAuthError = (data, config) => {
  // 退出登录接口不弹出任何信息
  if (!config?.url?.includes('/system/auth/logout')) {
    const messageText = RELOGIN_ERROR_CODES.has(data?.code)
      ? '您的账号在其他设备上登录，如非本人操作，请重新登录'
      : data?.message || '认证失败';
    message.warning(messageText, 5);
  }

  // 执行退出登录操作
  performLogout();
};

// 辅助函数：处理超时错误
const handleTimeoutError = error => {
  const isFileDownload = SPECIAL_URLS.some(url => error.config?.url?.includes(url));
  const messageText = isFileDownload
    ? '文件下载超时，请在文件中心查看记录'
    : '请求超时，请稍后再试';

  message.warning(messageText, 5);
};

/**
 * 统一请求方法
 * @param {string} url - 请求地址
 * @param {string} method - 请求方法
 * @param {Object} data - 请求数据
 * @param {boolean} [useParams=false] - 是否使用params传参
 */
export const request = async (url, method, data = {}, useParams = false) => {
  try {
    const config = {
      url,
      method,
      [useParams || method === 'get' ? 'params' : 'data']: data,
    };

    const response = await service.request(config);
    return response.data?.data; // 返回嵌套数据
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * 退出登录处理（导出供外部使用）
 */
export const failOut = () => {
  message.warning('登录状态已失效，请重新登录', 3);
  performLogout();
};

export default service;
